// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Cohorts
 * @package @geenius/analytics
 * @description Shared cohort-analysis helpers used by the runtime variants and
 * Convex queries to transform event streams into retention-oriented data sets.
 */

import type {
  AnalyticsEvent,
  CohortAnalysis,
  CohortBucket,
  CohortRetentionPoint,
} from "./types";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Optional inputs that tailor shared cohort analysis output.
 *
 * @property name Optional label applied to the generated cohort report.
 * @property intervals Retention checkpoints, in days, used when computing cohort curves.
 * @property cohortType Identity model used to group records as users or sessions.
 */
export interface CohortAnalysisOptions {
  name?: string;
  intervals?: number[];
  cohortType?: "user" | "session";
}

export type BehavioralCohortRule =
  | { kind: "event"; event: string }
  | { kind: "trait"; field: string; value: unknown }
  | { kind: "activity"; minEvents: number; since?: number };

export interface BehavioralCohortDefinition {
  id: string;
  name: string;
  rules: BehavioralCohortRule[];
  match?: "all" | "any";
}

export interface BehavioralCohortResult {
  id: string;
  name: string;
  size: number;
  identities: string[];
  eventCount: number;
  conversionRate: number;
  lastActivity: number;
}

export interface CohortComparisonResult {
  baselineId: string;
  comparisonId: string;
  sizeDelta: number;
  eventCountDelta: number;
  conversionRateDelta: number;
  lastActivityDelta: number;
}

function getIdentity(
  event: AnalyticsEvent,
  cohortType: "user" | "session",
): string {
  if (cohortType === "user") return event.userId ?? event.sessionId;
  return event.sessionId;
}

function dayKey(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function getProperty(event: AnalyticsEvent, field: string): unknown {
  if (field === "userId") return event.userId;
  if (field === "sessionId") return event.sessionId;
  return event.properties?.[field];
}

function matchesRule(
  events: AnalyticsEvent[],
  rule: BehavioralCohortRule,
): boolean {
  if (rule.kind === "event") {
    return events.some((event) => event.name === rule.event);
  }

  if (rule.kind === "trait") {
    return events.some(
      (event) => getProperty(event, rule.field) === rule.value,
    );
  }

  return (
    events.filter((event) => event.timestamp >= (rule.since ?? 0)).length >=
    rule.minEvents
  );
}

/**
 * Analyzes analytics events into retention-oriented cohort buckets.
 *
 * @param events Analytics events or page-derived events to group into cohorts.
 * @param options Optional naming, interval, and identity settings for the analysis.
 * @returns A normalized cohort analysis model ready for Convex storage and UI rendering.
 */
export function analyzeCohorts(
  events: AnalyticsEvent[],
  options: CohortAnalysisOptions = {},
): CohortAnalysis {
  const intervals = [
    ...new Set((options.intervals ?? [1, 7, 30]).filter((day) => day >= 0)),
  ].sort((a, b) => a - b);
  const cohortType = options.cohortType ?? "user";
  const name = options.name ?? "Cohort Analysis";
  const identities = new Map<string, AnalyticsEvent[]>();

  for (const event of events) {
    const identity = getIdentity(event, cohortType);
    const list = identities.get(identity) ?? [];
    list.push(event);
    identities.set(identity, list);
  }

  const cohortMap = new Map<string, Map<string, AnalyticsEvent[]>>();

  for (const [identity, identityEvents] of identities.entries()) {
    const sorted = [...identityEvents].sort(
      (a, b) => a.timestamp - b.timestamp,
    );
    const first = sorted[0];
    if (!first) continue;
    const cohort = dayKey(first.timestamp);
    const bucket = cohortMap.get(cohort) ?? new Map<string, AnalyticsEvent[]>();
    bucket.set(identity, sorted);
    cohortMap.set(cohort, bucket);
  }

  const cohorts: CohortBucket[] = [...cohortMap.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([cohort, members]) => {
      const membersList = [...members.values()];
      const firstSeen =
        membersList
          .map((list) => list[0]?.timestamp ?? 0)
          .filter(Boolean)
          .sort((a, b) => a - b)[0] ?? 0;
      const retained: CohortRetentionPoint[] = intervals.map((day) => {
        const retainedUsers = membersList.filter((list) => {
          const first = list[0]?.timestamp ?? 0;
          return list.some((event) => {
            const diffDays = Math.floor((event.timestamp - first) / DAY_MS);
            return diffDays === day;
          });
        }).length;
        const size = membersList.length;
        return {
          day,
          retainedUsers,
          retentionRate: size > 0 ? (retainedUsers / size) * 100 : 0,
        };
      });

      return {
        cohort,
        size: membersList.length,
        retained,
        lastActivity:
          membersList
            .flat()
            .map((event) => event.timestamp)
            .sort((a, b) => b - a)[0] ?? firstSeen,
      };
    });

  return {
    name,
    cohortType,
    intervals,
    cohorts,
    generatedAt: Date.now(),
  };
}

/**
 * Converts retained-user counts into retention points for a cohort bucket.
 *
 * @param size Total size of the cohort bucket.
 * @param points Raw retained-user counts by day.
 * @returns Retention points with computed percentages for each interval.
 */
export function createCohortRetentionPoints(
  size: number,
  points: Array<{ day: number; retainedUsers: number }>,
): CohortRetentionPoint[] {
  return points.map((point) => ({
    day: point.day,
    retainedUsers: point.retainedUsers,
    retentionRate: size > 0 ? (point.retainedUsers / size) * 100 : 0,
  }));
}

export function createBehavioralCohortDefinition(
  definition: BehavioralCohortDefinition,
): BehavioralCohortDefinition {
  return {
    ...definition,
    match: definition.match ?? "all",
  };
}

export function analyzeBehavioralCohorts(
  events: AnalyticsEvent[],
  definitions: BehavioralCohortDefinition[],
  options: Pick<CohortAnalysisOptions, "cohortType"> = {},
): BehavioralCohortResult[] {
  const cohortType = options.cohortType ?? "user";
  const identities = new Map<string, AnalyticsEvent[]>();

  for (const event of events) {
    const identity = getIdentity(event, cohortType);
    const list = identities.get(identity) ?? [];
    list.push(event);
    identities.set(identity, list);
  }

  return definitions.map((definition) => {
    const normalized = createBehavioralCohortDefinition(definition);
    const matched = [...identities.entries()].filter(([, identityEvents]) => {
      const results = normalized.rules.map((rule) =>
        matchesRule(identityEvents, rule),
      );
      return normalized.match === "any"
        ? results.some(Boolean)
        : results.every(Boolean);
    });
    const cohortEvents = matched.flatMap(
      ([, identityEvents]) => identityEvents,
    );
    const converted = matched.filter(([, identityEvents]) =>
      identityEvents.some((event) => event.name.includes("conversion")),
    ).length;

    return {
      id: normalized.id,
      name: normalized.name,
      size: matched.length,
      identities: matched.map(([identity]) => identity),
      eventCount: cohortEvents.length,
      conversionRate:
        matched.length > 0 ? (converted / matched.length) * 100 : 0,
      lastActivity:
        cohortEvents.map((event) => event.timestamp).sort((a, b) => b - a)[0] ??
        0,
    };
  });
}

export function compareBehavioralCohorts(
  baseline: BehavioralCohortResult,
  comparison: BehavioralCohortResult,
): CohortComparisonResult {
  return {
    baselineId: baseline.id,
    comparisonId: comparison.id,
    sizeDelta: comparison.size - baseline.size,
    eventCountDelta: comparison.eventCount - baseline.eventCount,
    conversionRateDelta: comparison.conversionRate - baseline.conversionRate,
    lastActivityDelta: comparison.lastActivity - baseline.lastActivity,
  };
}
